import React, { useState, useMemo } from 'react'
import { useAppData } from '../../context/AppContext'
import { usePermissions } from '../../hooks/usePermissions'
import { dataService } from '../../services/dataService'
import { currencyService } from '../../services/currencyService'
import { dateCalculationService } from '../../services/dateCalculationService'
import { allocationDistributionService } from '../../services/allocationDistributionService'
import { FolderOpen, UserCheck, DollarSign, TrendingUp, ArrowLeft, ArrowRight, AlertTriangle } from '../icons/Icons'
import s from './shared.module.css'
import ds from './Dashboard.module.css'

type ViewType = 'day' | 'week' | 'month' | 'quarter' | 'year'

const toUTCDateOnly = (date: Date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))

const normalizeEndUTC = (date: Date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999))

const parseDateOnlyUTC = (dateStr: string) => {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

export default function Dashboard() {
  const { customers, projects, resources, allocations, resourceRoleTypes, holidays, pendingApprovalsForMe, orgManagers, refreshData } = useAppData()
  const { isManager } = usePermissions()
  const [currentView, setCurrentView] = useState<ViewType>('month')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [recentPage, setRecentPage] = useState(1)
  const [deadlinePage, setDeadlinePage] = useState(1)
  const perPage = 4

  const [rejectTarget, setRejectTarget] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const handleApprove = async (requestId: string) => {
    setActionLoading(requestId)
    setActionError(null)
    try {
      debugger;
      await dataService.updateTimeOffRequest(requestId, {
        status: 'approved',
        rejection_reason: '',
      })
      await refreshData()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve request')
    } finally {
      setActionLoading(null)
    }
  }

  const handleConfirmReject = async () => {
    if (!rejectTarget) return
    setActionLoading(rejectTarget)
    setActionError(null)
    try {
      await dataService.updateTimeOffRequest(rejectTarget, {
        status: 'rejected',
        rejection_reason: rejectionReason,
      })
      await refreshData()
      setRejectTarget(null)
      setRejectionReason('')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reject request')
    } finally {
      setActionLoading(null)
    }
  }

  const getDateRange = () => {
    switch (currentView) {
      case 'day': return { startDate: currentDate, endDate: currentDate }
      case 'week': return { startDate: dateCalculationService.getStartOfWeek(currentDate), endDate: dateCalculationService.getEndOfWeek(currentDate) }
      case 'month': return { startDate: dateCalculationService.getStartOfMonth(currentDate), endDate: dateCalculationService.getEndOfMonth(currentDate) }
      case 'quarter': return { startDate: dateCalculationService.getStartOfQuarter(currentDate), endDate: dateCalculationService.getEndOfQuarter(currentDate) }
      case 'year': return { startDate: dateCalculationService.getStartOfYear(currentDate), endDate: dateCalculationService.getEndOfYear(currentDate) }
    }
  }

  const { startDate, endDate } = getDateRange()

  const navigate = (dir: -1 | 1) => {
    const d = new Date(currentDate)
    switch (currentView) {
      case 'day': d.setUTCDate(d.getUTCDate() + dir); break
      case 'week': d.setUTCDate(d.getUTCDate() + 7 * dir); break
      case 'month': d.setUTCMonth(d.getUTCMonth() + dir); break
      case 'quarter': d.setUTCMonth(d.getUTCMonth() + 3 * dir); break
      case 'year': d.setUTCFullYear(d.getUTCFullYear() + dir); break
    }
    setCurrentDate(d)
  }

  const viewStartUTC = toUTCDateOnly(startDate)
  const viewEndUTC = normalizeEndUTC(endDate)

  const activeProjects = projects.filter(p => p.status === 'Active')
  const tentativeProjects = projects.filter(p => p.status === 'Tentative')
  const activeResources = resources.filter(r => r.active)

  /*const periodRevenueByCurrency = useMemo(() => {
    return allocations.reduce((totals, allocation) => {
      const project = projects.find(p => p.id === allocation.project_id)
      if (!project || !allocation.billable) return totals
      if (project.status !== 'Active' && project.status !== 'Tentative') return totals
      const billingRate = allocation.billing_rate_at_allocation || resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)?.billing_rate || 0
      const currency = allocation.currency_at_allocation || resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)?.currency || 'USD'
      if (billingRate === 0) return totals
      const allocStart = parseDateOnlyUTC(allocation.allocation_start)
      const allocEnd = parseDateOnlyUTC(allocation.allocation_end)
      const overlapStart = allocStart > viewStartUTC ? allocStart : viewStartUTC
      const overlapEnd = allocEnd < viewEndUTC ? allocEnd : viewEndUTC
      if (overlapStart > overlapEnd) return totals
      const workingDays = dateCalculationService.getWorkingDaysInRange(overlapStart, overlapEnd, holidays)
      if (!workingDays) return totals
      totals[currency] = (totals[currency] || 0) + allocation.daily_hours * billingRate * workingDays
      return totals
    }, {} as Record<string, number>)
  }, [allocations, projects, resourceRoleTypes, holidays, viewStartUTC, viewEndUTC]) */
    const periodRevenueByCurrency = useMemo(() => {
    return allocations.reduce((totals, allocation) => {
      const project = projects.find(p => p.id === allocation.project_id)
      if (!project || !allocation.billable) return totals
      if (project.status !== 'Active' && project.status !== 'Tentative') return totals
      const billingRate = allocation.billing_rate_at_allocation || resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)?.billing_rate || 0
      const currency = allocation.currency_at_allocation || resourceRoleTypes.find(rt => rt.id === allocation.role_type_id)?.currency || 'USD'
      if (billingRate === 0) return totals
      const allocStart = parseDateOnlyUTC(allocation.allocation_start)
      const allocEnd = parseDateOnlyUTC(allocation.allocation_end)
      const overlapStart = allocStart > viewStartUTC ? allocStart : viewStartUTC
      const overlapEnd = allocEnd < viewEndUTC ? allocEnd : viewEndUTC
      if (overlapStart > overlapEnd) return totals
      // Use allocation distribution service to properly handle Daily, Weekly, and Monthly intervals
      const totalHours = allocationDistributionService.getHoursForDateRange(
        allocation.allocation_start,
        allocation.allocation_end,
        allocation.daily_hours,
        allocation.allocation_interval,
        overlapStart.toISOString().split('T')[0],
        overlapEnd.toISOString().split('T')[0],
        holidays
      )
      if (!totalHours) return totals
      totals[currency] = (totals[currency] || 0) + totalHours * billingRate
      return totals
    }, {} as Record<string, number>)
  }, [allocations, projects, resourceRoleTypes, holidays, viewStartUTC, viewEndUTC])


  const totalRevenueUSD = useMemo(() => {
    const currencies = Object.keys(periodRevenueByCurrency)
    if (!currencies.length) return 0
    return currencyService.calculateTotalRevenue(currencies.map(c => ({ amount: periodRevenueByCurrency[c], currency: c })), 'USD')
  }, [periodRevenueByCurrency])

  const hasWorkingDays = dateCalculationService.getWorkingDaysInRange(viewStartUTC, viewEndUTC, holidays) > 0

  const totalCapacity = useMemo(() => {
    const workingDays = dateCalculationService.getWorkingDaysInRange(viewStartUTC, viewEndUTC, holidays)
    return activeResources.reduce((t, r) => t + r.availability * workingDays, 0)
  }, [activeResources, holidays, viewStartUTC, viewEndUTC])

  /*const totalAllocatedHours = useMemo(() => {
    return allocations.reduce((total, allocation) => {
      const allocStart = parseDateOnlyUTC(allocation.allocation_start)
      const allocEnd = parseDateOnlyUTC(allocation.allocation_end)
      const overlapStart = allocStart > viewStartUTC ? allocStart : viewStartUTC
      const overlapEnd = allocEnd < viewEndUTC ? allocEnd : viewEndUTC
      if (overlapStart > overlapEnd) return total
      const workingDays = dateCalculationService.getWorkingDaysInRange(overlapStart, overlapEnd, holidays)
      return total + allocation.daily_hours * workingDays
    }, 0)
  }, [allocations, holidays, viewStartUTC, viewEndUTC]) */
    const totalAllocatedHours = useMemo(() => {
    return allocations.reduce((total, allocation) => {
      const allocStart = parseDateOnlyUTC(allocation.allocation_start)
      const allocEnd = parseDateOnlyUTC(allocation.allocation_end)
      const overlapStart = allocStart > viewStartUTC ? allocStart : viewStartUTC
      const overlapEnd = allocEnd < viewEndUTC ? allocEnd : viewEndUTC
      if (overlapStart > overlapEnd) return total
      // Use allocation distribution service to properly handle Daily, Weekly, and Monthly intervals
      const totalHours = allocationDistributionService.getHoursForDateRange(
        allocation.allocation_start,
        allocation.allocation_end,
        allocation.daily_hours,
        allocation.allocation_interval,
        overlapStart.toISOString().split('T')[0],
        overlapEnd.toISOString().split('T')[0],
        holidays
      )
      return total + totalHours
    }, 0)
  }, [allocations, holidays, viewStartUTC, viewEndUTC])


  const utilizationRate = totalCapacity > 0 ? (totalAllocatedHours / totalCapacity) * 100 : 0

  /*const overbookedResources = useMemo(() => {
    const overbooked = new Set<string>()
    activeResources.forEach(resource => {
      let checkDate = new Date(viewStartUTC)
      while (checkDate <= viewEndUTC) {
        if (!dateCalculationService.isWorkingDay(checkDate, holidays)) {
          checkDate = new Date(checkDate.getTime() + 86400000)
          continue
        }
        const dailyHours = allocations
          .filter(a => {
            if (a.resource_id !== resource.id) return false
            const s = parseDateOnlyUTC(a.allocation_start)
            const e = parseDateOnlyUTC(a.allocation_end)
            return checkDate >= s && checkDate <= e
          })
          .reduce((sum, a) => sum + a.daily_hours, 0)
        if (dailyHours > resource.availability) {
          overbooked.add(resource.id)
          break
        }
        checkDate = new Date(checkDate.getTime() + 86400000)
      }
    })
    return overbooked
  }, [activeResources, allocations, holidays, viewStartUTC, viewEndUTC]) */
    const overbookedResources = useMemo(() => {
    const overbooked = new Set<string>()
    activeResources.forEach(resource => {
      let checkDate = new Date(viewStartUTC)
      while (checkDate <= viewEndUTC) {
        if (!dateCalculationService.isWorkingDay(checkDate, holidays)) {
          checkDate = new Date(checkDate.getTime() + 86400000)
          continue
        }
        const checkDateStr = checkDate.toISOString().split('T')[0]
        const dailyHours = allocations
          .filter(a => {
            if (a.resource_id !== resource.id) return false
            const s = parseDateOnlyUTC(a.allocation_start)
            const e = parseDateOnlyUTC(a.allocation_end)
            return checkDate >= s && checkDate <= e
          })
          .reduce((sum, a) => {
            // Calculate the actual distributed hours for this specific day
            const dailyHoursMap = allocationDistributionService.calculateDailyHoursFromAllocation(
              a.allocation_start,
              a.allocation_end,
              a.daily_hours,
              a.allocation_interval,
              holidays
            )
            return sum + (dailyHoursMap[checkDateStr] || 0)
          }, 0)
        if (dailyHours > resource.availability) {
          overbooked.add(resource.id)
          break
        }
        checkDate = new Date(checkDate.getTime() + 86400000)
      }
    })
    return overbooked
  }, [activeResources, allocations, holidays, viewStartUTC, viewEndUTC])


  /*const utilizedResources = useMemo(() => {
    return activeResources.map(resource => {
      const workingDays = dateCalculationService.getWorkingDaysInRange(viewStartUTC, viewEndUTC, holidays)
      const periodCapacity = resource.availability * workingDays
      const periodAllocatedHours = allocations.reduce((total, allocation) => {
        if (allocation.resource_id !== resource.id) return total
        const allocStart = parseDateOnlyUTC(allocation.allocation_start)
        const allocEnd = parseDateOnlyUTC(allocation.allocation_end)
        const overlapStart = allocStart > viewStartUTC ? allocStart : viewStartUTC
        const overlapEnd = allocEnd < viewEndUTC ? allocEnd : viewEndUTC
        if (overlapStart > overlapEnd) return total
        const wd = dateCalculationService.getWorkingDaysInRange(overlapStart, overlapEnd, holidays)
        return total + allocation.daily_hours * wd
      }, 0)
      return { resource, periodCapacity, periodAllocatedHours, utilizationPercent: periodCapacity > 0 ? (periodAllocatedHours / periodCapacity) * 100 : 0, workingDays }
    }).filter(r => r.periodAllocatedHours > 0).sort((a, b) => b.periodAllocatedHours - a.periodAllocatedHours).slice(0, 8)
  }, [activeResources, allocations, holidays, viewStartUTC, viewEndUTC]) */
    const utilizedResources = useMemo(() => {
    return activeResources.map(resource => {
      const workingDays = dateCalculationService.getWorkingDaysInRange(viewStartUTC, viewEndUTC, holidays)
      const periodCapacity = resource.availability * workingDays
      const periodAllocatedHours = allocations.reduce((total, allocation) => {
        if (allocation.resource_id !== resource.id) return total
        const allocStart = parseDateOnlyUTC(allocation.allocation_start)
        const allocEnd = parseDateOnlyUTC(allocation.allocation_end)
        const overlapStart = allocStart > viewStartUTC ? allocStart : viewStartUTC
        const overlapEnd = allocEnd < viewEndUTC ? allocEnd : viewEndUTC
        if (overlapStart > overlapEnd) return total
        // Use allocation distribution service to properly handle Daily, Weekly, and Monthly intervals
        const totalHours = allocationDistributionService.getHoursForDateRange(
          allocation.allocation_start,
          allocation.allocation_end,
          allocation.daily_hours,
          allocation.allocation_interval,
          overlapStart.toISOString().split('T')[0],
          overlapEnd.toISOString().split('T')[0],
          holidays
        )
        return total + totalHours
      }, 0)
      return { resource, periodCapacity, periodAllocatedHours, utilizationPercent: periodCapacity > 0 ? (periodAllocatedHours / periodCapacity) * 100 : 0, workingDays }
    }).filter(r => r.periodAllocatedHours > 0).sort((a, b) => b.periodAllocatedHours - a.periodAllocatedHours).slice(0, 8)
  }, [activeResources, allocations, holidays, viewStartUTC, viewEndUTC])


  const todayUTC = toUTCDateOnly(new Date())
  const sortedRecentProjects = useMemo(() => [...projects].sort((a, b) => new Date(b.created_date || '').getTime() - new Date(a.created_date || '').getTime()), [projects])
  const upcomingDeadlines = useMemo(() =>
    projects
      .filter(p => p.status === 'Active' || p.status === 'Tentative')
      .map(p => ({ ...p, daysLeft: Math.ceil((parseDateOnlyUTC(p.end_date).getTime() - todayUTC.getTime()) / 86400000) }))
      .sort((a, b) => a.daysLeft - b.daysLeft)
  , [projects, todayUTC])

  const recentSlice = sortedRecentProjects.slice((recentPage - 1) * perPage, recentPage * perPage)
  const deadlineSlice = upcomingDeadlines.slice((deadlinePage - 1) * perPage, deadlinePage * perPage)
  const recentPages = Math.ceil(sortedRecentProjects.length / perPage)
  const deadlinePages = Math.ceil(upcomingDeadlines.length / perPage)

  const statusColor = (status: string) => {
    if (status === 'Active') return '#107c10'
    if (status === 'Tentative') return '#0078d4'
    if (status === 'On Hold') return '#ca5010'
    return '#605e5c'
  }

  return (
    <div>
      <div className={s.pageHeader}>
        <div>
          <h2 className={s.pageTitle}>Dashboard</h2>
          <p className={s.pageSubtitle}>Resource utilization and project status at a glance</p>
        </div>
      </div>

      <div className={ds.timeNav}>
        <div className={ds.viewBtns}>
          {(['day', 'week', 'month', 'quarter', 'year'] as ViewType[]).map(v => (
            <button key={v} className={`${ds.viewBtn} ${currentView === v ? ds.active : ''}`} onClick={() => setCurrentView(v)}>
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <div className={ds.navBtns}>
          <button className={ds.navArrow} onClick={() => navigate(-1)}><ArrowLeft size={14} /></button>
          <span className={ds.periodLabel}>{dateCalculationService.formatDateRange(startDate, endDate, currentView)}</span>
          <button className={ds.navArrow} onClick={() => navigate(1)}><ArrowRight size={14} /></button>
          <button className={ds.todayBtn} onClick={() => setCurrentDate(new Date())}>Today</button>
        </div>
      </div>

      <div className={s.statsGrid} style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
        {[
          { label: 'Active Projects', value: activeProjects.length, icon: <FolderOpen size={18} /> },
          { label: 'Tentative Projects', value: tentativeProjects.length, icon: <FolderOpen size={18} /> },
          { label: 'Active Resources', value: activeResources.length, icon: <UserCheck size={18} /> },
          { label: 'Revenue Forecast', value: currencyService.formatCurrency(totalRevenueUSD, 'USD', 0), icon: <DollarSign size={18} /> },
          { label: 'Utilization Rate', value: `${Math.round(utilizationRate)}%`, icon: <TrendingUp size={18} /> },
        ].map(stat => (
          <div key={stat.label} className={s.statCard}>
            <div className={s.statIcon}>{stat.icon}</div>
            <div>
              <p className={s.statLabel}>{stat.label}</p>
              <p className={s.statValue}>{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {overbookedResources.size > 0 && (
        <div className={ds.alert}>
          <AlertTriangle size={16} />
          <span>{overbookedResources.size} resource(s) are overbooked in the selected period.</span>
        </div>
      )}

      <div className={ds.twoCol}>
        <div className={s.card}>
          <div className={s.cardHeader}>
            <span className={s.cardTitle}>Recent Projects</span>
          </div>
          <table className={s.table}>
            <thead><tr><th>Project</th><th>Customer</th><th>Status</th></tr></thead>
            <tbody>
              {recentSlice.length === 0 && <tr><td colSpan={3} className={s.emptyState}>No projects</td></tr>}
              {recentSlice.map(p => {
                const customer = customers.find(c => c.id === p.customer_id)
                return (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.title}</td>
                    <td>{customer?.title || '—'}</td>
                    <td><span style={{ color: statusColor(p.status), fontWeight: 600, fontSize: 12 }}>{p.status}</span></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {recentPages > 1 && (
            <div className={ds.pager}>
              <button disabled={recentPage === 1} onClick={() => setRecentPage(p => p - 1)}><ArrowLeft size={12} /></button>
              <span>{recentPage} / {recentPages}</span>
              <button disabled={recentPage === recentPages} onClick={() => setRecentPage(p => p + 1)}><ArrowRight size={12} /></button>
            </div>
          )}
        </div>

        <div className={s.card}>
          <div className={s.cardHeader}>
            <span className={s.cardTitle}>Upcoming Deadlines</span>
          </div>
          <table className={s.table}>
            <thead><tr><th>Project</th><th>End Date</th><th>Days Left</th></tr></thead>
            <tbody>
              {deadlineSlice.length === 0 && <tr><td colSpan={3} className={s.emptyState}>No deadlines</td></tr>}
              {deadlineSlice.map(p => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 600 }}>{p.title}</td>
                  <td>{p.end_date}</td>
                  <td>
                    <span style={{
                      color: p.daysLeft < 0 ? '#d13438' : p.daysLeft < 30 ? '#ca5010' : '#107c10',
                      fontWeight: 600, fontSize: 12
                    }}>
                      {p.daysLeft < 0 ? `${Math.abs(p.daysLeft)}d overdue` : `${p.daysLeft}d`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {deadlinePages > 1 && (
            <div className={ds.pager}>
              <button disabled={deadlinePage === 1} onClick={() => setDeadlinePage(p => p - 1)}><ArrowLeft size={12} /></button>
              <span>{deadlinePage} / {deadlinePages}</span>
              <button disabled={deadlinePage === deadlinePages} onClick={() => setDeadlinePage(p => p + 1)}><ArrowRight size={12} /></button>
            </div>
          )}
        </div>
      </div>

      {isManager && pendingApprovalsForMe.length > 0 && (
        <div className={s.card} style={{ marginBottom: 24, border: '1px solid #ffd97d', background: 'rgba(255, 244, 206, 0.7)' }}>
          <div className={s.cardHeader} style={{ borderBottom: '1px solid #ffd97d' }}>
            <span className={s.cardTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={16} style={{ color: '#ca5010' }} />
              Pending Time-Off Approvals
            </span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#ca5010', background: 'rgba(202, 80, 16, 0.1)', padding: '2px 10px', borderRadius: 9 }}>
              {pendingApprovalsForMe.length} pending
            </span>
          </div>
          {actionError && (
            <div style={{ padding: '8px 20px', color: '#d13438', fontSize: 12, fontWeight: 600 }}>
              {actionError}
            </div>
          )}
          <div style={{ padding: '4px 0' }}>
            {pendingApprovalsForMe.map(req => {
              const resource = resources.find(r => r.id === req.resource_id)
              const manager = orgManagers.find(m => m.id === resource?.manager_id)
              const start = new Date(req.start_date + 'T00:00:00Z')
              const end = new Date(req.end_date + 'T00:00:00Z')
              const days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1
              const isRejecting = rejectTarget === req.id
              const loading = actionLoading === req.id
              return (
                <div key={req.id} style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255, 217, 125, 0.4)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: '#323130' }}>{resource?.title || 'Unknown Resource'}</span>
                      <span style={{ fontSize: 12, color: '#605e5c' }}>
                        {(req.time_off_type || 'unknown').replace(/_/g, ' ')} &bull; {req.start_date} – {req.end_date} ({days} day{days !== 1 ? 's' : ''})
                        {!manager && <span style={{ marginLeft: 6, color: '#a19f9d', fontStyle: 'italic' }}>— no assigned manager</span>}
                      </span>
                      {req.notes && <span style={{ fontSize: 12, color: '#605e5c', fontStyle: 'italic' }}>{req.notes}</span>}
                    </div>
                    {!isRejecting && (
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button
                          disabled={loading}
                          onClick={() => handleApprove(req.id)}
                          style={{ padding: '6px 14px', background: '#107c10', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
                        >
                          {loading ? '...' : 'Approve'}
                        </button>
                        <button
                          disabled={loading}
                          onClick={() => { setRejectTarget(req.id); setRejectionReason('') }}
                          style={{ padding: '6px 14px', background: 'transparent', color: '#d13438', border: '1px solid #d13438', borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                  {isRejecting && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        autoFocus
                        value={rejectionReason}
                        onChange={e => setRejectionReason(e.target.value)}
                        placeholder="Reason for rejection (required)"
                        style={{ flex: 1, minWidth: 200, padding: '6px 10px', border: '1px solid #d13438', borderRadius: 4, fontSize: 12 }}
                      />
                      <button
                        disabled={!rejectionReason.trim() || !!actionLoading}
                        onClick={handleConfirmReject}
                        style={{ padding: '6px 14px', background: '#d13438', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: (!rejectionReason.trim() || !!actionLoading) ? 'not-allowed' : 'pointer', opacity: (!rejectionReason.trim() || !!actionLoading) ? 0.6 : 1 }}
                      >
                        {actionLoading === rejectTarget ? '...' : 'Confirm Reject'}
                      </button>
                      <button
                        onClick={() => { setRejectTarget(null); setRejectionReason('') }}
                        style={{ padding: '6px 10px', background: 'transparent', color: '#605e5c', border: '1px solid #edebe9', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className={s.card} style={{ marginTop: 20 }}>
        <div className={s.cardHeader}>
          <span className={s.cardTitle}>Resource Utilization — {currentView.charAt(0).toUpperCase() + currentView.slice(1)} View</span>
          <span style={{ fontSize: 12, color: '#605e5c' }}>{Math.round(totalAllocatedHours)}h / {Math.round(totalCapacity)}h</span>
        </div>
        {!hasWorkingDays ? (
          <div className={s.emptyState}>No working days in the selected period</div>
        ) : utilizedResources.length === 0 ? (
          <div className={s.emptyState}>No resources allocated in this period</div>
        ) : (
          <div className={ds.utilGrid}>
            {utilizedResources.map(({ resource, periodCapacity, periodAllocatedHours, utilizationPercent }) => {
              const isOver = overbookedResources.has(resource.id)
              const barColor = isOver ? '#d13438' : utilizationPercent >= 100 ? '#107c10' : utilizationPercent >= 60 ? '#0078d4' : '#ca5010'
              return (
                <div key={resource.id} className={ds.utilCard}>
                  <div className={ds.utilAvatar}><UserCheck size={18} /></div>
                  <div className={ds.utilName}>{resource.title}</div>
                  <div className={ds.utilRole}>{resource.primary_role}</div>
                  <div className={ds.utilBar}>
                    <div className={ds.utilFill} style={{ width: `${Math.min(utilizationPercent, 100)}%`, background: barColor }} />
                  </div>
                  <div className={ds.utilPct} style={{ color: barColor }}>{Math.round(utilizationPercent)}%</div>
                  <div className={ds.utilHours}>{Math.round(periodAllocatedHours)}h / {Math.round(periodCapacity)}h</div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
